import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import candidateService from '@/services/candidateService';
import { newsletterService } from '@/services/newsletterService';
import Loader from '@/components/Loader';
import useAuth from '@/hooks/stores/useAuth';
import useToastStore from '@/components/Common/Toast/toastStore';

const isOptedIn = (value) => value === true || value === 1 || value === '1';

const CandidateDashboard = () => {
  const [candidate, setCandidate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [savingOptIn, setSavingOptIn] = useState(false);
  const navigate = useNavigate();
  const { userID } = useAuth();
  const { showToast } = useToastStore;

  const fetchCandidate = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await candidateService.getCandidateProfile();
      setCandidate(data);
      setEmailOptIn(isOptedIn(data?.newsletter?.email_opt_in));
      setSmsOptIn(isOptedIn(data?.newsletter?.sms_opt_in));
    } catch (err) {
      console.error('Error fetching candidate:', err);
      setError(err.message || 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCandidate();
  }, [fetchCandidate]);

  const handleOptInChange = async (field, checked) => {
    if (!userID) {
      showToast({ message: 'Unable to save preferences. Please try again.', type: 'error' });
      return;
    }

    const nextEmailOptIn = field === 'email' ? checked : emailOptIn;
    const nextSmsOptIn = field === 'sms' ? checked : smsOptIn;

    if (field === 'email') setEmailOptIn(checked);
    else setSmsOptIn(checked);

    setSavingOptIn(true);
    try {
      await newsletterService.updatePreferences(userID, {
        email_opt_in: nextEmailOptIn,
        sms_opt_in: nextSmsOptIn,
      });
      showToast({ message: 'Marketing preferences updated successfully!', type: 'success' });
      setCandidate((prev) => ({
        ...prev,
        newsletter: {
          ...prev?.newsletter,
          email_opt_in: nextEmailOptIn ? 1 : 0,
          sms_opt_in: nextSmsOptIn ? 1 : 0,
        },
      }));
    } catch (err) {
      if (field === 'email') setEmailOptIn(!checked);
      else setSmsOptIn(!checked);
      showToast({ message: err.message || 'Failed to update marketing preferences', type: 'error' });
    } finally {
      setSavingOptIn(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-card">
        <Loader bgColor="white" />
      </div>
    );
  }

  if (error && !candidate) {
    return (
      <div className="w-[90%] mx-auto mt-10 mb-6 text-red-500">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="w-[90%] mx-auto mt-10 mb-6 max-w-[1280px]">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-foreground mb-2">Dashboard</h1>
        <p className="text-muted-foreground">Welcome back! Here's your profile overview.</p>
      </div>

      <div className="bg-gradient-to-br from-card to-muted p-8 rounded-2xl shadow-xl border border-border transition-all duration-300 hover:shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-1 h-8 bg-muted rounded-full"></div>
          <h2 className="text-xl font-semibold text-foreground">Personal Information</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="group">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
              Name
            </label>
            <div className="relative">
              <div className="w-full px-4 py-3.5 bg-muted border border-border rounded-xl text-foreground font-medium shadow-sm transition-all duration-200 group-hover:border-input group-hover:shadow-md">
                {candidate?.name || 'N/A'}
              </div>
            </div>
          </div>

          <div className="group">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
              Email
            </label>
            <div className="relative">
              <div className="w-full px-4 py-3.5 bg-muted border border-border rounded-xl text-foreground font-medium shadow-sm transition-all duration-200 group-hover:border-input group-hover:shadow-md">
                {candidate?.email || 'N/A'}
              </div>
            </div>
          </div>

          <div className="group md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
              Total Applications
            </label>
            <div
              className="relative cursor-pointer"
              onClick={() => navigate('/candidate/applications')}
            >
              <div className="w-full px-4 py-3.5 bg-muted border border-border rounded-xl text-foreground font-semibold shadow-sm transition-all duration-200 group-hover:border-input group-hover:shadow-lg group-hover:-translate-y-0.5 transform">
                <span className="text-2xl font-bold text-foreground">
                  {candidate?.applications_count || '0'}
                </span>
              </div>
            </div>
          </div>

          <div className="group md:col-span-2">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 block">
              Marketing Preferences
            </label>
            <div className="w-full px-4 py-4 bg-[#f4f4f5] border border-[#e4e4e7] rounded-xl shadow-sm">
              <div className="flex flex-col gap-4">
                <div className="flex items-center space-x-3">
                  <input
                    id="dashboard_email_opt_in"
                    type="checkbox"
                    checked={emailOptIn}
                    disabled={savingOptIn}
                    onChange={(e) => handleOptInChange('email', e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 items-center"
                  />
                  <label
                    htmlFor="dashboard_email_opt_in"
                    className="text-base text-gray-700 cursor-pointer"
                  >
                    I agree to receive marketing communications by email
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    id="dashboard_sms_opt_in"
                    type="checkbox"
                    checked={smsOptIn}
                    disabled={savingOptIn}
                    onChange={(e) => handleOptInChange('sms', e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 items-center"
                  />
                  <label
                    htmlFor="dashboard_sms_opt_in"
                    className="text-base text-gray-700 cursor-pointer"
                  >
                    I agree to receive marketing communications by SMS
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateDashboard;
